import { ReactNode, useCallback, useContext, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Button, LoadingGroup, Modal, Spinner } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Heading } from "capitalrx-components-2.0/lib/typography";

import { useListMemberDependentsQuery } from "@/App/api/member";
import { AccumulatorCard } from "@/App/components/AccumulatorCard/AccumulatorCard";
import { MemberEligibilityContext } from "@/App/context";
import {
  Accumulators,
  DisplayNetworkStatuses,
  DisplayScopes,
  MoneyAccumulationValues,
  MutableAccumulationData,
} from "@/App/types/accumulation";

import "./CommercialBenefits.scss";

export interface CommercialBenefitsProps {
  readonly displayNetworkStatus: DisplayNetworkStatuses;
  readonly displayScope: DisplayScopes;
  readonly accumulations: MutableAccumulationData;
  readonly hasMedical: boolean;
  readonly hasPharmacy: boolean;
  readonly isLoading: boolean;
}

interface AccumulationsSectionProps {
  readonly accumulations: MutableAccumulationData;
  readonly displayScope: DisplayScopes;
  readonly displayNetworkStatus: DisplayNetworkStatuses;
  readonly isFamilyCoverage?: boolean;
}

const AccumulationsSummary = (props: AccumulationsSectionProps) => {
  const { accumulations, displayNetworkStatus, displayScope, isFamilyCoverage } = props;
  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalTitle, setModalTitle] = useState<string>("");
  const [modalContent, setModalContent] = useState<string | ReactNode>("");

  const { t: translate } = useTranslation();

  const { columns } = useResponsive({
    columns: {
      mobile: 12,
      tablet: 6,
      desktop: 4,
    },
  });

  const accums = accumulations[displayNetworkStatus][displayScope];

  const medical: Partial<Record<Accumulators, MoneyAccumulationValues>> = {
    deductible: accums.deductible.find((accum) => accum.appliesTo.includes("medical")),
    outOfPocket: accums.outOfPocket.find((accum) => accum.appliesTo.includes("medical")),
    planPay: accums.planPay.find((accum) => accum.appliesTo.includes("medical")),
  };
  const pharmacy: Partial<Record<Accumulators, MoneyAccumulationValues>> = {
    deductible: accums.deductible.find((accum) => accum.appliesTo.includes("pharmacy")),
    outOfPocket: accums.outOfPocket.find((accum) => accum.appliesTo.includes("pharmacy")),
    planPay: accums.planPay.find((accum) => accum.appliesTo.includes("pharmacy")),
  };

  const hasMedical = !!(medical.deductible || medical.outOfPocket || medical.planPay);
  const hasPharmacy = !!(pharmacy.deductible || pharmacy.outOfPocket || pharmacy.planPay);
  const shouldEnforceEmbedded = displayScope === DisplayScopes.Individual && isFamilyCoverage === true;

  const medicalTagFallback = (name: Accumulators): string[] => {
    if (hasPharmacy && !pharmacy[name]) {
      return ["medical", "pharmacy"];
    }
    return ["medical"];
  };

  const showMedicalTile = (name: Accumulators): boolean => {
    const isEmbedded = medical[name]?.isEmbedded;

    if (shouldEnforceEmbedded && !isEmbedded) {
      return false;
    }
    return hasMedical;
  };

  const showPharmacyTile = (name: Accumulators): boolean => {
    // If is unified and data is not combined or it's not unified
    const isUnified = hasPharmacy && hasMedical;
    const notCombined = pharmacy[name] !== medical[name];
    const inNetwork = displayNetworkStatus === DisplayNetworkStatuses.In;
    const isEmbedded = pharmacy[name]?.isEmbedded;

    if (!isEmbedded && shouldEnforceEmbedded) {
      return false;
    }

    return inNetwork && ((isUnified && notCombined) || !isUnified);
  };

  const hasAnyDeductible = showMedicalTile(Accumulators.Deductible) || showPharmacyTile(Accumulators.Deductible);
  const hasAnyOutOfPocket = showMedicalTile(Accumulators.OutOfPocket) || showPharmacyTile(Accumulators.OutOfPocket);
  const hasAnyPlanPay = showMedicalTile(Accumulators.PlanPay) || showPharmacyTile(Accumulators.PlanPay);

  const modalCloseHandler = useCallback(() => {
    setShowModal(false);
  }, []);

  return (
    <>
      <Modal.Confirm visible={showModal} title={modalTitle} onClose={modalCloseHandler}>
        {modalContent}
      </Modal.Confirm>
      <Stack spacing="medium">
        {hasAnyDeductible && (
          <Shelf spacing="mini">
            <Heading>{translate("BENEFITS.commercialBenefits.deductibles")}</Heading>
            <Button
              appearance="text"
              icon="question-circle"
              onClick={() => {
                setModalTitle(translate("BENEFITS.commercialBenefits.deductibles"));
                setModalContent(
                  <Trans
                    i18nKey="BENEFITS.commercialBenefits.deductiblesModalContent"
                    components={{
                      p: <p />,
                    }}
                  />,
                );
                setShowModal(true);
              }}
            />
            <Shelf.Spacer />
          </Shelf>
        )}
        <Grid spacing="medium">
          {showMedicalTile(Accumulators.Deductible) && (
            <Grid.Span columns={columns}>
              <AccumulatorCard
                displayScope={displayScope}
                name={Accumulators.Deductible}
                accumulator={medical.deductible}
                tags={medical.deductible?.appliesTo ?? medicalTagFallback(Accumulators.Deductible)}
                planHasAccumulator={!!medical.deductible}
              />
            </Grid.Span>
          )}
          {showPharmacyTile(Accumulators.Deductible) && (
            <Grid.Span columns={columns}>
              <AccumulatorCard
                displayScope={displayScope}
                name={Accumulators.Deductible}
                accumulator={pharmacy.deductible}
                tags={pharmacy.deductible?.appliesTo ?? ["pharmacy"]}
                planHasAccumulator={!!pharmacy.deductible}
              />
            </Grid.Span>
          )}
        </Grid>
        {hasAnyOutOfPocket && (
          <Shelf spacing="mini">
            <Heading>{translate("BENEFITS.commercialBenefits.outOfPockets")}</Heading>
            <Button
              appearance="text"
              icon="question-circle"
              onClick={() => {
                setModalTitle(translate("BENEFITS.commercialBenefits.outOfPockets"));
                setModalContent(
                  <Trans
                    i18nKey="BENEFITS.commercialBenefits.outOfPocketsModalContent"
                    components={{
                      p: <p />,
                    }}
                  />,
                );
                setShowModal(true);
              }}
            />
            <Shelf.Spacer />
          </Shelf>
        )}
        <Grid spacing="medium">
          {showMedicalTile(Accumulators.OutOfPocket) && (
            <Grid.Span columns={columns}>
              <AccumulatorCard
                displayScope={displayScope}
                name={Accumulators.OutOfPocket}
                accumulator={medical.outOfPocket}
                tags={medical.outOfPocket?.appliesTo ?? medicalTagFallback(Accumulators.OutOfPocket)}
                planHasAccumulator={!!medical.outOfPocket}
              />
            </Grid.Span>
          )}
          {showPharmacyTile(Accumulators.OutOfPocket) && (
            <Grid.Span columns={columns}>
              <AccumulatorCard
                displayScope={displayScope}
                name={Accumulators.OutOfPocket}
                accumulator={pharmacy.outOfPocket}
                tags={pharmacy.outOfPocket?.appliesTo ?? ["pharmacy"]}
                planHasAccumulator={!!pharmacy.outOfPocket}
              />
            </Grid.Span>
          )}
        </Grid>
        {hasAnyPlanPay && (
          <Shelf spacing="mini">
            <Heading>{translate("BENEFITS.commercialBenefits.maximums")}</Heading>
            <Button
              appearance="text"
              icon="question-circle"
              onClick={() => {
                setModalTitle(translate("BENEFITS.commercialBenefits.maximums"));
                setModalContent(
                  <Trans
                    i18nKey="BENEFITS.commercialBenefits.maximumsModalContent"
                    components={{
                      p: <p />,
                    }}
                  />,
                );
                setShowModal(true);
              }}
            />
            <Shelf.Spacer />
          </Shelf>
        )}
        <Grid spacing="medium">
          {showMedicalTile(Accumulators.PlanPay) && (
            <Grid.Span columns={columns}>
              <AccumulatorCard
                displayScope={displayScope}
                name={Accumulators.PlanPay}
                accumulator={medical.planPay}
                tags={medical.planPay?.appliesTo ?? medicalTagFallback(Accumulators.PlanPay)}
                planHasAccumulator={!!medical.planPay}
              />
            </Grid.Span>
          )}
          {showPharmacyTile(Accumulators.PlanPay) && (
            <Grid.Span columns={columns}>
              <AccumulatorCard
                displayScope={displayScope}
                name={Accumulators.PlanPay}
                accumulator={pharmacy.planPay}
                tags={pharmacy.planPay?.appliesTo ?? ["pharmacy"]}
                planHasAccumulator={!!pharmacy.planPay}
              />
            </Grid.Span>
          )}
        </Grid>
      </Stack>
    </>
  );
};

export const CommercialBenefits = (props: CommercialBenefitsProps) => {
  const { displayNetworkStatus, displayScope, accumulations, isLoading } = props;

  const { memberEligibility } = useContext(MemberEligibilityContext);

  const { data: dependents } = useListMemberDependentsQuery();

  const hasDependents = useMemo(() => {
    return (dependents?.results ?? []).length > 0;
  }, [dependents]);

  let isFamilyCoverage = false;
  if (memberEligibility?.member?.coverage_type) {
    isFamilyCoverage = !["01", "06", "08"].includes(memberEligibility.member.coverage_type);
  } else {
    isFamilyCoverage = hasDependents;
  }

  return (
    <LoadingGroup loading={isLoading}>
      <LoadingGroup.Placeholder
        fallback={
          <Shelf>
            <Shelf.Spacer />
            <Spinner size="large" />
            <Shelf.Spacer />
          </Shelf>
        }
      >
        <AccumulationsSummary
          accumulations={accumulations}
          displayNetworkStatus={displayNetworkStatus}
          displayScope={displayScope}
          isFamilyCoverage={isFamilyCoverage}
        />
      </LoadingGroup.Placeholder>
    </LoadingGroup>
  );
};
